import { ArrowIcon } from "@/Icons/Arrow";
import { FetchedHelpSections } from "@/Utils/api/sanity/types";
import { mapImportSection } from "@/Utils/helpSections";
import { useConfig } from "@/api/hooks/useConfig";

import React from "react";
import Collapsible from "react-collapsible";

import { PortableText, PortableTextProps } from "@portabletext/react";
import { twMerge } from "tailwind-merge";

import { ImportStep } from "./types";

const Trigger: React.FC<{ title: string; open?: boolean }> = ({
  title,
  open = false,
}) => {
  return (
    <div className="text-grey6 flex justify-between">
      {title}{" "}
      <div
        className={twMerge("text-grey6 m-2 transform", open && "rotate-180")}
      >
        <ArrowIcon />
      </div>
    </div>
  );
};

const Content: React.FC<PortableTextProps> = (props) => {
  return (
    <PortableText
      {...props}
      components={{
        marks: {
          link: ({ children, value }) => {
            return (
              <a href={value.href} className="underline">
                {children}
              </a>
            );
          },
        },
        list: {
          bullet: ({ children }) => {
            return <ul className="ml-4 list-inside list-disc">{children}</ul>;
          },
          number: ({ children }) => {
            return (
              <ol className="ml-4 list-inside list-decimal">{children}</ol>
            );
          },
        },
      }}
    />
  );
};

export const HelpSection: React.FC<{
  activeStep: ImportStep;
  helpSections: FetchedHelpSections | null;
}> = ({ activeStep, helpSections }) => {
  const { isMWM, config } = useConfig();

  if (!helpSections) return null;

  const doc = helpSections.find((section) => {
    return (
      mapImportSection({
        isMWM,
        section,
        useWasteSuctionSystem: config.useWasteSuctionSystem,
      }) === activeStep.helpSectionKey
    );
  });

  if (doc) {
    return (
      <div className="my-4 mb-12 w-2/3">
        {doc.description?.length && (
          <div className="my-4 text-sm">
            <Content value={doc.description} />
          </div>
        )}
        {doc.sections?.map((section, index) => (
          <Collapsible
            className="border-grey3 border-b py-2"
            openedClassName="pt-2"
            key={index}
            trigger={<Trigger title={section.title} />}
            triggerWhenOpen={<Trigger open title={section.title} />}
          >
            <div className="border-grey3 border-b py-2">
              <Content value={section.content} />
            </div>
            {section.subSections?.map((subSection, subIndex) => (
              <Collapsible
                className="border-grey3 ml-4 border-b py-2"
                openedClassName="ml-4 pt-2"
                key={subIndex}
                trigger={<Trigger title={subSection.title} />}
                triggerWhenOpen={<Trigger open title={subSection.title} />}
              >
                <div className="border-grey3 border-b py-2">
                  <Content value={subSection.content} />
                </div>
              </Collapsible>
            ))}
          </Collapsible>
        ))}
      </div>
    );
  }

  return <></>;
};
